// ============================================================
// frontend/src/lib/useDebouncedSave.ts
// ============================================================
// Auto-saves a value `delay` ms after the user stops changing it.
//
// Used for session notes (and similar long-form fields) where
// asking the user to click "Save" every time is annoying.
//
// HOW IT WORKS:
// 1. Tracks the "last saved" version of the value.
// 2. When the value differs from the saved version, schedules a save.
// 3. If the value changes again before the timer fires, resets the timer.
// 4. Fires the save when the value has been stable for `delay` ms.
//
// USAGE:
//   const { saving, saved, isDirty, error } = useDebouncedSave(
//     notes,
//     (val) => api.put(`/sessions/${id}`, { notes: val }),
//     800
//   );
// ============================================================

import { useState, useRef, useEffect, useCallback } from 'react';

interface UseDebouncedSaveReturn {
  saving: boolean;
  saved: boolean;
  error: Error | null;
  /** True if value differs from last saved version (unsaved changes pending). */
  isDirty: boolean;
}

export function useDebouncedSave<T>(
  value: T,
  saveFn: (v: T) => Promise<unknown>,
  delay = 800
): UseDebouncedSaveReturn {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // What's been saved on the server (so we can compare).
  // Initialize with the current value, since on mount we assume it came from the server.
  const savedValueRef = useRef<T>(value);

  // Always-fresh ref to the save function. Prevents stale closures.
  // Without this, putting saveFn in useEffect deps would cause an infinite loop.
  const saveFnRef = useRef(saveFn);
  useEffect(() => { saveFnRef.current = saveFn; }, [saveFn]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    // No change → nothing to save
    if (value === savedValueRef.current) return;

    // Cancel previous timer if value changed again
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      const valueToSave = value;  // Capture value at this point, not stale
      setSaving(true);
      setError(null);
      try {
        await saveFnRef.current(valueToSave);
        if (!mountedRef.current) return;
        savedValueRef.current = valueToSave;
        setSaving(false);
        setSaved(true);
        setTimeout(() => {
          if (mountedRef.current) setSaved(false);
        }, 1200);
      } catch (err) {
        if (!mountedRef.current) return;
        setSaving(false);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delay]);

  return {
    saving, saved, error,
    isDirty: value !== savedValueRef.current,
  };
}
